import type { ScheduleBoard } from '@homecare/contracts';
import { Button, Card, EmptyState, PageHeader, Spinner, StatusPill } from '@homecare/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useMemo, useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi } from '../../lib/api-hooks';
import { useSession } from '../../lib/session';
import { shiftWeek, weekText } from '../../lib/schedule-time';

import { BoardGrid } from './board-grid';
import { layoutBoard, LEGEND } from './board-layout';
import { BookVisit } from './book-visit';
import { BulkChanges } from './bulk-changes';
import { CalloutsPanel } from './callouts-panel';
import { VisitDrawer } from './visit-drawer';

/**
 * The schedule board — the screen the product is judged on (slice-03 §8).
 *
 * One request draws it: GET /v1/schedule/board returns the agency's week,
 * its today, the caregiver rows and the visits. The page adds nothing to that
 * but layout, so a week is never shown with days from one zone and cards from
 * another.
 *
 * ## What else is on the page
 *
 * RECOVERY §2: "the board was never the slow part; a 500-row unvirtualised
 * list beside it was." The call-outs panel lists a week's call-outs, which is
 * tens, not hundreds; nothing else on this page renders a list.
 */
export function SchedulePage(): ReactNode {
  const api = useApi();
  const permissions = useSession((state) => state.permissions);
  const [weekOf, setWeekOf] = useState<string | null>(null);
  const [openVisit, setOpenVisit] = useState<string | null>(null);
  const [booking, setBooking] = useState(false);

  const board = useQuery({
    queryKey: ['schedule', 'board', weekOf],
    queryFn: () => api.get<ScheduleBoard>(`/v1/schedule/board${weekOf ? `?weekOf=${weekOf}` : ''}`),
    staleTime: 0,
    placeholderData: (previous) => previous,
  });
  const data = board.data;
  const layout = useMemo(() => (data ? layoutBoard(data) : null), [data]);

  const canBook = permissions.includes('schedule:create');
  const canChange = permissions.includes('schedule:update');
  const [bulk, setBulk] = useState(false);
  const isThisWeek = data ? data.today >= data.weekStart && data.today <= data.weekEnd : true;

  return (
    <section className="grid gap-5">
      <PageHeader
        title="Schedule"
        meta={
          data && layout
            ? `${weekText(data.weekStart, data.weekEnd)} · ${layout.shifts} visits · ${
                layout.open.cells.flat().filter((card) => card.part === 'starts').length
              } open`
            : 'The week, caregivers down the side and days across.'
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        {data && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekOf(shiftWeek(data.weekStart, -1))}
            >
              Previous week
            </Button>
            {isThisWeek ? (
              <Button variant="secondary" size="sm" disabled disabledReason="You are on this week.">
                This week
              </Button>
            ) : (
              <Button variant="secondary" size="sm" onClick={() => setWeekOf(null)}>
                This week
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setWeekOf(shiftWeek(data.weekStart, 1))}
            >
              Next week
            </Button>
          </>
        )}
        <span className="flex-1" />
        <Link
          to="/schedule/overrides"
          className="text-brand hover:text-brand-hover text-sm font-medium"
        >
          Override report
        </Link>
        {canChange && (
          <Button variant="secondary" onClick={() => setBulk(true)}>
            Bulk changes
          </Button>
        )}
        {canBook && <Button onClick={() => setBooking(true)}>Book a visit</Button>}
      </div>

      <ul
        className="flex flex-wrap items-center gap-2"
        aria-label="What the colours and marks mean"
      >
        {LEGEND.map((state) => (
          <li key={state.label}>
            <StatusPill tone={state.tone}>{state.label}</StatusPill>
          </li>
        ))}
        <li className="text-ink-3 text-xs">
          A dashed card is an overnight carrying on from the day before.
        </li>
      </ul>

      <ErrorNote error={board.error} nextStep="Reload the page to try again." />

      {board.isPending ? (
        <p className="text-ink-2 flex items-center gap-2 text-sm" role="status">
          <Spinner /> Loading the week…
        </p>
      ) : data && layout ? (
        <>
          {layout.rows.length === 0 && layout.shifts === 0 ? (
            <Card>
              <EmptyState
                title="Nothing on the board yet"
                explanation="Active caregivers appear here as rows, and visits as cards on their days. Book a visit, or a weekly pattern, to start the week."
              />
            </Card>
          ) : (
            <BoardGrid layout={layout} today={data.today} onOpen={setOpenVisit} />
          )}
          <CalloutsPanel
            from={data.weekStart}
            to={data.weekEnd}
            timeZone={data.timezone}
            onOpen={setOpenVisit}
          />
          <VisitDrawer
            visitId={openVisit}
            timeZone={data.timezone}
            caregivers={data.caregivers}
            onClose={() => setOpenVisit(null)}
          />
          {canBook && (
            <BookVisit
              open={booking}
              onClose={() => setBooking(false)}
              defaultDate={isThisWeek ? data.today : data.weekStart}
              agencyTimeZone={data.timezone}
              caregivers={data.caregivers}
            />
          )}
          {canChange && (
            <BulkChanges
              open={bulk}
              onClose={() => setBulk(false)}
              weekStart={data.weekStart}
              caregivers={data.caregivers}
            />
          )}
        </>
      ) : null}
    </section>
  );
}
